import { captureException } from '@sentry/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { referralService } from '@/services/database';
import type { ReferralActivityPage, ReferralActivityType, ReferralAnalyticsInterval } from '@/services/database';

export const referralSummaryQueryKey = ['referralSummary'] as const;
export const referralSummaryIntervalQueryKey = (interval: ReferralAnalyticsInterval) =>
    [...referralSummaryQueryKey, interval] as const;

export const useReferralSummary = (interval: ReferralAnalyticsInterval = '30d') =>
    useQuery({
        queryFn: async ({ signal }) => {
            try {
                return await referralService.getSummary(interval, signal);
            } catch (error) {
                captureException(error, { tags: { operation: 'referral.summary' } });
                throw error;
            }
        },
        queryKey: referralSummaryIntervalQueryKey(interval),
        retry: 2,
        staleTime: 5 * 60 * 1_000,
    });

export const referralActivityQueryKey = (type: ReferralActivityType) => ['referralActivity', type] as const;

export const useReferralActivity = <TType extends ReferralActivityType>(type: TType, pageSize = 50) =>
    useInfiniteQuery<
        ReferralActivityPage<TType>,
        Error,
        InfiniteData<ReferralActivityPage<TType>>,
        ReturnType<typeof referralActivityQueryKey>,
        string | null
    >({
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        initialPageParam: null,
        queryFn: async ({ pageParam, signal }) => {
            try {
                return await referralService.getActivity(type, pageParam, pageSize, signal);
            } catch (error) {
                captureException(error, { tags: { operation: `referral.activity.${type}` } });
                throw error;
            }
        },
        queryKey: referralActivityQueryKey(type),
        staleTime: 60 * 1_000,
    });
